import React from "react";
import { Col } from "react-bootstrap";
import { useNavigate } from "react-router-dom";

function TotalGroupsRecord({ data }) {
  const navigate = useNavigate();

  /*
  |--------------------------------------------------------------------------
  | CARD CLICK
  |--------------------------------------------------------------------------
  */

  const handleCardClick = (key) => {
    navigate(`/act-group-details/${key}`);
  };


  /*
  |--------------------------------------------------------------------------
  | NUMBER FORMAT
  |--------------------------------------------------------------------------
  */

  const formatNumber = (number) => {
    if (
      number === null ||
      number === undefined ||
      number === ""
    ) {
      return 0;
    }

    return Number(number).toLocaleString(
      "en-PK"
    );
  };


  /*
  |--------------------------------------------------------------------------
  | MONEY FORMAT
  |--------------------------------------------------------------------------
  */

  const formatMoney = (number) => {
    if (
      number === null ||
      number === undefined ||
      number === ""
    ) {
      return "PKR 0";
    }

    return `PKR ${Number(
      number
    ).toLocaleString("en-PK", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    })}`;
  };


  /*
  |--------------------------------------------------------------------------
  | PERCENT FORMAT
  |--------------------------------------------------------------------------
  */

  const formatPercent = (number) => {
    if (
      number === null ||
      number === undefined ||
      number === ""
    ) {
      return "0%";
    }

    return `${Number(number).toFixed(
      2
    )}%`;
  };


  /*
  |--------------------------------------------------------------------------
  | COUNT + PERCENT
  |--------------------------------------------------------------------------
  */

  const countWithPercent = (
    count,
    percent
  ) => {
    return `${formatNumber(
      count
    )} (${formatPercent(percent)})`;
  };


  /*
  |--------------------------------------------------------------------------
  | CARDS
  |--------------------------------------------------------------------------
  */

  const cards = [

    // ==========================================================
    // GROUP COUNTS
    // ==========================================================

    {
      key: "total_groups",
      title: "Total Groups",
      icon: "groups",
      number: formatNumber(
        data?.total_groups
      ),
    },

    {
      key: "male_groups",
      title: "Total Male Groups",
      icon: "male",
      number: formatNumber(
        data?.total_male_groups
      ),
    },

    {
      key: "female_groups",
      title: "Total Female Groups",
      icon: "female",
      number: formatNumber(
        data?.total_female_groups
      ),
    },


    // ==========================================================
    // MEMBERS
    // ==========================================================

    {
      key: "male_members",
      title: "Total Male Members",
      icon: "man",
      number: formatNumber(
        data?.total_male_members
      ),
    },

    {
      key: "female_members",
      title: "Total Female Members",
      icon: "woman",
      number: formatNumber(
        data?.total_female_members
      ),
    },

    {
      key: "total_members",
      title: "Total Members",
      icon: "group",
      number: formatNumber(
        data?.total_members
      ),
    },

    {
      key: "avg_members",
      title: "Avg Members / Group",
      icon: "functions",

      /*
       * Backend calculates this now.
       */

      number: Number(
        data?.avg_members || 0
      ).toFixed(2),
    },


    // ==========================================================
    // GROUP STATUS
    // ==========================================================

    {
      key: "groups_25",
      title: "Groups with 25 Members",
      icon: "verified",
      number: formatNumber(
        data?.groups_25
      ),
    },

    {
      key: "groups_25_percent",
      title: "% Groups Reached Max",
      icon: "percent",

      number: formatPercent(
        data?.groups_25_percent
      ),
    },


    // ==========================================================
    // FAMILY COUNTS
    // ==========================================================

    {
      key: "rescued",
      title: "Total Rescued Families",
      icon: "check_circle",

      number: countWithPercent(
        data?.rescued_count,
        data?.rescued_percent
      ),
    },

    {
      key: "to_be_rescued",
      title: "To Be Rescued Families",
      icon: "pending",

      number: countWithPercent(
        data?.to_be_rescued_count,
        data?.to_be_rescued_percent
      ),
    },

    {
      key: "community",
      title: "Community Families",
      icon: "diversity_3",

      number: countWithPercent(
        data?.community_count,
        data?.community_percent
      ),
    },


    // ==========================================================
    // SAVINGS
    // ==========================================================

    {
      key: "total_savings",
      title: "Total Savings",
      icon: "savings",

      number: formatMoney(
        data?.total_savings
      ),
    },

    {
      key: "avg_saving",
      title: "Average Saving Amount",
      icon: "account_balance",

      number: formatMoney(
        data?.avg_saving
      ),
    },

    {
      key: "avg_saving_group",
      title: "Avg Saving / Group",
      icon: "calculate",

      number: formatMoney(
        data?.avg_saving_group
      ),
    },

    {
      key: "avg_saving_member",
      title: "Avg Saving / Member",
      icon: "calculate",

      number: formatMoney(
        data?.avg_saving_member
      ),
    },


    // ==========================================================
    // MEETINGS
    // ==========================================================

    {
      key: "avg_meetings",
      title: "Avg Meetings / Group",
      icon: "event_repeat",

      number: Number(
        data?.avg_meetings || 0
      ).toFixed(2),
    },


    // ==========================================================
    // MATERIAL DISTRIBUTION
    // ==========================================================

    {
      key: "passbook",
      title: "Pass Book Delivered",
      icon: "menu_book",

      number: countWithPercent(
        data?.passbook_delivered,
        data?.passbook_percent
      ),
    },

    {
      key: "saving_box",
      title: "Saving Box Delivered",
      icon: "inventory",

      number: countWithPercent(
        data?.saving_box_delivered,
        data?.saving_box_percent
      ),
    },

    {
      key: "minute_book",
      title: "Minute Book Delivered",
      icon: "description",

      number: countWithPercent(
        data?.minute_book_delivered,
        data?.minute_book_percent
      ),
    },

    {
      key: "loan_ledger",
      title: "Loan Ledger Delivered",
      icon: "receipt_long",

      number: countWithPercent(
        data?.loan_ledger_delivered,
        data?.loan_ledger_percent
      ),
    },


    // ==========================================================
    // LOANS FROM GROUP SAVINGS
    // ==========================================================

    {
      key: "total_loans",
      title: "Loans Issued to Members",
      icon: "payments",
      number: formatNumber(
        data?.total_loans_count
      ),
    },

    {
      key: "loan_amount",
      title: "Total Loan Amount",
      icon: "request_quote",
      number: formatMoney(
        data?.total_loans_amount
      ),
    },

    {
      key: "small_business",
      title: "Small Businesses Established",
      icon: "storefront",
      number: formatNumber(
        data?.small_business_count
      ),
    },

    {
      key: "rickshaw",
      title: "Rickshaw Loans",
      icon: "local_taxi",
      number: formatNumber(
        data?.rickshaw_count
      ),
    },
  ];


  /*
  |--------------------------------------------------------------------------
  | RENDER
  |--------------------------------------------------------------------------
  */

  return (
    <>
      {cards.map((item) => (

        <Col
          xl={3}
          lg={4}
          md={6}
          sm={12}
          key={item.key}
        >

          <div
            className="mc-ecommerce-card"
            onClick={() =>
              handleCardClick(
                item.key
              )
            }
            style={{
              background:
                "linear-gradient(to right, rgba(0,0,0,0.85), #077834)",

              cursor: "pointer",
            }}
          >

            {/* Trend Icon */}

            <i
              className="
                mc-ecommerce-card-trend
                material-icons
                text-success
              "
            >
              {item.trend ||
                "analytics"}
            </i>


            <div className="mc-ecommerce-card-head">

              {/* Title + Number */}

              <h4 className="mc-ecommerce-card-meta">

                <span>
                  {item.title}
                </span>

                <span>
                  {item.number ??
                    "-"}
                </span>

              </h4>


              {/* Main Icon */}

              <i
                className="
                  mc-ecommerce-card-icon
                  material-icons
                  text-light
                "
              >
                {item.icon}
              </i>

            </div>

          </div>

        </Col>

      ))}
    </>
  );
}

export default TotalGroupsRecord;